import { z } from "zod";

import { assertNever } from "../utils/assertNever.ts";

// Design specs for new device options: https://www.figma.com/design/ewlqvnY79qWz0QGgCIkngD/Frame--Options-menu?node-id=156-2751&t=zaOz5O3j5HrUz8eh-4
export const SupportedScreens = {
  // Web
  web: [1140, 956], // same as "desktop"
  // Desktop
  desktop: [1140, 956], // same as "MacBook Air 13"
  "desktop-large": [1920, 1080],
  "desktop-medium": [1440, 1024], // default fallback
  "desktop-small": [1366, 768],
  // Tablet
  tablet: [820, 1180], // same as "iPad"
  "ipad-tablet-horizontal": [1194, 834],
  "ipad-tablet-vertical": [834, 1194],
  "android-tablet-horizontal": [1280, 800],
  "android-tablet-vertical": [800, 1280],
  // Mobile
  mobile_ios: [402, 874], // same as "iPhone 17"
  mobile_android: [360, 780], // same as "Galaxy S25"
  "iphone-vertical": [393, 852],
  "iphone-horizontal": [852, 393],
  "iphone-x-vertical": [375, 812],
  "iphone-x-horizontal": [812, 375],
  "android-vertical": [360, 800],
  "android-horizontal": [800, 360],
  "android-small-vertical": [360, 640],
  "android-small-horizontal": [640, 360],
} as const satisfies Record<string, [number, number]>;

export const ScreenType = z.enum(
  Object.keys(SupportedScreens) as [
    keyof typeof SupportedScreens,
    ...Array<keyof typeof SupportedScreens>,
  ],
);

export type ScreenType = z.infer<typeof ScreenType>;

export type OperatingSystem = "ios" | "android" | "web";
export type DeviceType = "mobile" | "tablet" | "desktop";

export const getOperatingSystemFromScreenType = (
  screenType: ScreenType,
): OperatingSystem => {
  if (
    screenType === "mobile_ios" ||
    screenType.includes("iphone") ||
    screenType.includes("ipad")
  )
    return "ios";
  if (screenType === "mobile_android" || screenType.includes("android"))
    return "android";
  return "web";
};

export const getDeviceTypeFromScreenType = (
  screenType: ScreenType,
): DeviceType => {
  if (screenType.includes("ipad") || screenType.includes("tablet"))
    return "tablet";
  if (
    screenType.startsWith("mobile_") ||
    screenType.includes("iphone") ||
    screenType.includes("android")
  )
    return "mobile";
  return "desktop";
};

export const getDeviceLabelFromScreenType = (
  screenType: ScreenType,
): string => {
  if (screenType === "web") return "web";

  const os = getOperatingSystemFromScreenType(screenType);
  const deviceType = getDeviceTypeFromScreenType(screenType);

  switch (os) {
    case "ios":
      return `iOS ${deviceType}`;
    case "android":
      return `Android ${deviceType}`;
    case "web":
      return deviceType;
    default:
      assertNever(os);
  }
};
